/** One explicit confirmation releases an immutable paid Book venue. Intro stays separate. */
import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useEditorialMediaUrl } from "@/lib/useEditorialMediaUrl";
import { resolveSpreadThumbnailV2 } from "./editorialSpreadDisplayV2.mjs";
import { EditorialBookDestinationPickerV1 } from "./EditorialBookDestinationPickerV1";

const QA_RUNTIME_MATERIAL_ID = "0f26cb7d-f6ce-484c-b9a0-596b33e60419";
const PREFIX = "/admin/editorial/spreads";

function authorStateLabel(state) {
  if (state?.hidden) return "Скрыто";
  if (state?.scheduled_snapshot_id) return state.published_snapshot_id ? "Запланировано обновление" : "Запланировано";
  if (state?.published_snapshot_id) return "Утверждено";
  return "Черновик";
}

function readableError(error) {
  const code = error?.response?.data?.detail?.code;
  if (code === "spread_changed_after_preview" || code === "spread_changed_after_approval") {
    return "Разворот изменился после проверки. Сохраните изменения и откройте предпросмотр снова.";
  }
  if (code === "spread_publication_version_conflict") {
    return "Состояние публикации изменилось. Обновите его и повторите действие.";
  }
  if (code === "spread_preview_required_or_expired") {
    return "Проверка разворота устарела. Откройте предпросмотр снова.";
  }
  if (code === "spread_qa_runtime_material_forbidden") {
    return "Служебный QA-материал нельзя публиковать.";
  }
  if (code === "spread_private_content_keys_forbidden") {
    return "В содержании обнаружены приватные служебные поля. Проверьте материал.";
  }
  if (code?.startsWith("release_") || code?.startsWith("placement_")) {
    if (code.includes("revision")) return "Состояние статьи изменилось. Закройте окно и проверьте место заново.";
    if (code.includes("stale")) return "Статья изменилась после проверки. Сохраните её и откройте публикацию заново.";
    if (code.includes("occupied")) return "Эта карточка уже содержит другой материал. Выберите свою или новую карточку.";
    if (code.includes("media")) return "Не удалось подтвердить фотографию статьи. Публикация не выполнена.";
    if (code.includes("scheduled")) return "У статьи есть скрытая или запланированная версия. Выпуск сейчас недоступен.";
    return "Публикация не выполнена. Проверьте статью и выбранное место в книге.";
  }
  return formatApiError(error);
}

function previewHeading(block) {
  const c = block?.content || {};
  return c.headline || c.title || c.recipe?.title ||
    c.extra?.historical_editorial?.title ||
    c.historical_editorial?.title ||
    block?.master_type || "Разворот";
}

// The existing first story belongs to the root of Tome I, before all four spaces.
// Never send it through the category placement flow: that would create a duplicate.
const INTRO_MATERIAL_ID = "9c60f83f-b5ab-438b-b27e-09f41480a620";
const ALMOND_PRODUCT_ID = "78917116-d963-45dd-946c-7797db939790";

function isTomeIntro(block) {
  // The entire introductory author material has one special Book destination.
  // Never let another MASTER added to it silently create a category placement.
  return block?.material_id === INTRO_MATERIAL_ID;
}

function TomeIntroLocationV1() {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState({ loading: false, volume: null, page: null, error: "" });

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setResult({ loading: true, volume: null, page: null, error: "" });
    (async () => {
      const { data: volumes } = await api.get("/admin/book/volumes");
      const matching = (Array.isArray(volumes) ? volumes : []).filter(
        (v) => v.product_id === ALMOND_PRODUCT_ID
      );
      if (matching.length !== 1) throw new Error("Том I не найден однозначно.");
      const volume = matching[0];
      const { data: pages } = await api.get(
        `/admin/book/pages?volume_id=${encodeURIComponent(volume.id)}`
      );
      const root = (Array.isArray(pages) ? pages : []).filter((p) =>
        p.editorial_material_id === INTRO_MATERIAL_ID &&
        p.volume_id === volume.id && p.parent_kind === "volume" &&
        p.parent_id === volume.id && p.section_id == null && p.subsection_id == null
      );
      if (root.length !== 1 || !root[0]?.id) {
        throw new Error("Не удалось подтвердить существующее вступление.");
      }
      if (active) setResult({ loading: false, volume, page: root[0], error: "" });
    })().catch(() => {
      if (active) setResult({ loading: false, volume: null, page: null,
        error: "Не удалось проверить место вступления. Новая страница не создавалась." });
    });
    return () => { active = false; };
  }, [open]);

  return (
    <div data-testid="editorial-v2-intro-location">
      <button type="button" onClick={() => setOpen(true)}
        className="rounded-xl border border-espresso/25 bg-white px-4 py-2 text-sm text-espresso hover:bg-cream"
        data-testid="editorial-v2-intro-location-open">
        Место в книге
      </button>
      {open && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/55 px-4 py-5"
          data-testid="editorial-v2-intro-location-dialog">
          <div className="w-full max-w-lg rounded-3xl bg-[#FAF5EC] p-5 text-espresso shadow-xl">
            <h2 className="font-serif text-2xl">Вступление Тома I</h2>
            <p className="mt-3 text-sm">
              Том I «Миндаль» → Вступление → «Тихо скрипнула старая дверь..»
            </p>
            <p className="mt-2 text-xs text-espresso/65">
              Это вступление находится перед четырьмя пространствами книги.
              Повторно выбирать категорию и создавать карточку не нужно.
            </p>
            {result.loading && <p className="mt-3 text-sm" role="status">Проверяем место в книге…</p>}
            {result.error && <p className="mt-3 text-sm text-red-800" role="alert">{result.error}</p>}
            {result.page && result.volume && (
              <div className="mt-4 space-y-3" data-testid="editorial-v2-intro-root-confirmed">
                <p className="text-sm">Страница уже связана с Томом.
                  {result.page.status !== "published" ? " Пока это черновик для предпросмотра." : " Страница опубликована."}
                </p>
                <a
                  href={`/book/${encodeURIComponent(result.volume.slug || result.volume.id)}/pages/${encodeURIComponent(result.page.id)}`}
                  className="inline-flex rounded-xl border border-espresso/25 bg-white px-4 py-2 text-sm"
                  data-testid="editorial-v2-intro-owner-reader">
                  Открыть в книге
                </a>
              </div>
            )}
            <p className="mt-4 text-xs text-espresso/60">
              Выпуск читателям ещё не подключён. Это окно не публикует статью и не изменяет данные.
            </p>
            <div className="mt-4 flex justify-end">
              <button type="button" onClick={() => setOpen(false)}
                className="rounded-xl border border-espresso/25 px-4 py-2 text-sm"
                data-testid="editorial-v2-intro-location-close">Закрыть</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function EditorialSpreadPublicationControlsV1({ block, dirty = false }) {
  return isTomeIntro(block)
    ? <TomeIntroLocationV1 />
    : <OrdinarySpreadPublicationControlsV1 block={block} dirty={dirty} />;
}

function OrdinarySpreadPublicationControlsV1({ block, dirty = false }) {
  const blockId = block?.id;
  const materialId = block?.material_id;
  const qa = materialId === QA_RUNTIME_MATERIAL_ID;
  const supported = ["EDITORIAL_STORY", "RECIPE"].includes(block?.master_type);
  const [opened, setOpened] = useState(false);
  const [preview, setPreview] = useState(null);
  const [placements, setPlacements] = useState([]);
  const [bookDestination, setBookDestination] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [result, setResult] = useState(null);
  const photo = useEditorialMediaUrl(resolveSpreadThumbnailV2(preview?.block?.content || {}));
  const selectDestination = useCallback((value) => { setBookDestination(value); setResult(null); }, []);
  useEffect(() => { if (dirty) { setPreview(null); setReady(false); } }, [dirty]);
  useEffect(() => { setOpened(false); setResult(null); setPreview(null); }, [blockId]);
  const selected = placements.find((p) => p.book_volume_id === bookDestination?.book_volume_id
    && p.section_id === bookDestination?.section_id && p.subsection_id === bookDestination?.subsection_id
    && (!bookDestination?.page_id || p.page_id === bookDestination.page_id));
  const open = async () => {
    if (qa || !supported) return;
    setOpened(true); setReady(false); setBusy(true); setResult(null); setBookDestination(null);
    try {
      const [checked, locations] = await Promise.all([
        api.get(`${PREFIX}/${encodeURIComponent(blockId)}/preview`),
        api.get(`/admin/editorial/materials/${encodeURIComponent(materialId)}/book-placements`),
      ]);
      setPreview(checked.data); setPlacements(locations.data.items || []); setReady(!dirty);
    } catch (error) { toast.error(readableError(error)); }
    finally { setBusy(false); }
  };
  const submit = async () => {
    if (busy || dirty || !ready || !preview || !bookDestination) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/editorial/materials/${encodeURIComponent(materialId)}/book-release`, {
        block_id: blockId, preview_token: preview.preview_token,
        destination: bookDestination, expected_revision: selected?.release_revision || 0,
      });
      if (data.mode !== "paid_book_release" || data.released !== true || !data.reader_path?.startsWith("/book/")) {
        throw new Error("Не удалось подтвердить выпуск статьи в книгу.");
      }
      setPlacements((items) => [...items.filter((p) => p.id !== data.placement.id), data.placement]);
      setResult(data); setOpened(false); toast.success("Статья опубликована");
    } catch (error) { toast.error(readableError(error)); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!selected || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/editorial/materials/${encodeURIComponent(materialId)}/book-placements/${encodeURIComponent(selected.id)}/revoke`, {
        expected_revision: selected.release_revision,
      });
      setPlacements((items) => items.map((p) => p.id === selected.id ? data.placement : p));
      setResult(null); toast.success("Статья скрыта в выбранном месте книги");
    } catch (error) { toast.error(readableError(error)); }
    finally { setBusy(false); }
  };
  return (
    <div data-spread-publication-controls="true" data-testid="editorial-v2-spread-publish">
      <button type="button" onClick={open} disabled={qa || !supported}
        className="rounded-xl border border-espresso/25 bg-white px-4 py-2 text-sm"
        data-testid="editorial-v2-spread-publish-open">Опубликовать</button>
      {!supported && !qa && <p className="mt-2 text-xs">Выпуск этого типа разворота пока недоступен.</p>}
      {result?.released && <a href={result.reader_path} className="ml-3 underline"
        data-testid="editorial-book-release-open">Открыть в книге</a>}
      {opened && <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/55 px-4 py-5"
        data-testid="editorial-v2-spread-publish-dialog">
        <div className="w-full max-w-lg overflow-y-auto rounded-3xl bg-[#FAF5EC] p-5 text-espresso shadow-xl" style={{maxHeight:"88vh"}}>
          <h2 className="font-serif text-2xl">Опубликовать статью</h2>
          <EditorialBookDestinationPickerV1 onSelectionChange={selectDestination} disabled={busy}
            materialId={materialId} materialTitle={previewHeading(block)} />
          {busy && <p role="status">Проверяем сохранённую статью…</p>}
          {dirty && <p role="alert" data-testid="editorial-v2-spread-unsaved-guard">Сначала сохраните изменения статьи.</p>}
          {preview && <div data-testid="editorial-v2-spread-confirm-preview" className="my-3">
            {photo && <img src={photo} alt="" className="max-h-36 w-full rounded-lg object-cover" />}
            <p className="font-serif text-lg">{previewHeading(preview.block)}</p>
          </div>}
          <p className="my-3 text-xs">Публикация сейчас в выбранном месте книги. Расписание пока недоступно.</p>
          <div className="flex flex-wrap justify-end gap-2">
            {selected?.release_kind === "paid_master_v1" && selected.status === "published" &&
              <button type="button" onClick={revoke} disabled={busy} data-testid="editorial-v2-spread-hide"
                className="rounded-xl border px-4 py-2">Скрыть в книге</button>}
            <button type="button" onClick={() => setOpened(false)} disabled={busy}
              className="rounded-xl border px-4 py-2" data-testid="editorial-v2-spread-publish-close">Закрыть</button>
            <button type="button" onClick={submit} disabled={busy || dirty || !ready || !bookDestination}
              className="rounded-xl bg-espresso px-4 py-2 text-cream disabled:opacity-50"
              data-testid="editorial-v2-spread-publish-confirm">{busy ? "Публикация…" : "Опубликовать"}</button>
          </div>
        </div>
      </div>}
    </div>
  );
}
